import React, { useEffect, useRef } from 'react';
import { Descriptions, Modal, Alert, Image } from 'antd';
import { useCurrentTrip } from '../hooks/useCurrentTrip';

const TripsViewModal = ({ isOpen, onRequestClose }) => {
    const { currentTrip } = useCurrentTrip();
    const mapContainerRef = useRef(null);

    const handleCancel = () => {
        onRequestClose(false);
    };

    useEffect(() => {
        // Initialize the map only for completed trips
        if (isOpen && currentTrip?.status === 'Done') {
            const initializeMap = () => {
                const mapOptions = {
                    center: {
                        lat: currentTrip.startPoint.latitude,
                        lng: currentTrip.startPoint.longitude,
                    },
                    zoom: 12,
                };

                const mapInstance = new window.google.maps.Map(mapContainerRef.current, mapOptions);

                // Display route between startPoint and endPoint
                const directionsService = new window.google.maps.DirectionsService();
                const directionsRenderer = new window.google.maps.DirectionsRenderer({ map: mapInstance });

                const start = new window.google.maps.LatLng(
                    currentTrip.startPoint.latitude,
                    currentTrip.startPoint.longitude
                );
                const end = new window.google.maps.LatLng(
                    currentTrip.endPoint.latitude,
                    currentTrip.endPoint.longitude
                );

                const request = {
                    origin: start,
                    destination: end,
                    travelMode: window.google.maps.TravelMode.DRIVING,
                };

                directionsService.route(request, (result, status) => {
                    if (status === window.google.maps.DirectionsStatus.OK) {
                        directionsRenderer.setDirections(result);
                    }
                });
            };

            if (window.google) initializeMap();
            else {
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places`;
                script.async = true;
                script.defer = true;
                script.addEventListener('load', initializeMap);
                document.body.appendChild(script);
            }
        }
    }, [isOpen, currentTrip]);

    return (
        <Modal width={800} bodyStyle={{ height: 632 }} open={isOpen} onCancel={handleCancel} footer={null}>
            <h3 style={{ marginBottom: 10 }}>Trip details #{currentTrip?.logicalId}</h3>

            {/* Show alert if trip is still running */}
            {currentTrip?.status === 'Running' && (
                <Alert
                    message="Trip is still in progress"
                    description="You can only view completed trip details after the trip is done."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 8 }}
                />
            )}

            <Descriptions bordered style={{ borderRadius: '8px 8px 0 0' }} column={1}>
                <Descriptions.Item label="Fullname Rider">
                    {currentTrip?.riderId?.gender === 'Male' ? 'Mr. ' : 'Mme. '}
                    {currentTrip?.riderId?.fullName}
                </Descriptions.Item>
                <Descriptions.Item label="Rider Email">{currentTrip?.riderId?.email}</Descriptions.Item>
                <Descriptions.Item label="QrCode Vehicule">{currentTrip?.vehiculeId?.qrcode}</Descriptions.Item>
                <Descriptions.Item label="Vehicule Drop Photo">
                    {currentTrip?.dropPhoto?.url ? (
                        <Image width={100} height={100} src={currentTrip?.dropPhoto?.url} alt="Drop photo" />
                    ) : (
                        <div
                            style={{
                                alignItems: 'center',
                                color: '#999',
                            }}
                        >
                            No drop photo available
                        </div>
                    )}
                </Descriptions.Item>
            </Descriptions>

            {/* Map or placeholder */}
            <div
                style={{
                    width: '100%',
                    height: '43%',
                    borderRadius: '0 0 8px 8px',
                    marginTop: '16px',
                    background: currentTrip?.status === 'Done' ? 'none' : '#fafafa',
                    border: currentTrip?.status === 'Done' ? 'none' : '1px dashed #ccc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                    textAlign: 'center',
                    padding: '20px',
                }}
                ref={currentTrip?.status === 'Done' ? mapContainerRef : null}
            >
                {currentTrip?.status !== 'Done' && (
                    <span>
                        🕓 The destination has not been reached yet. The route map will appear once the trip is
                        completed.
                    </span>
                )}
            </div>
        </Modal>
    );
};

export default TripsViewModal;
